/**
 * RoleModal Component
 * 
 * Clean modal for creating and editing roles
 * Features:
 * - Form validation
 * - Keyboard support (ESC to close)
 * - Accessibility features
 * - Automatic permission structure initialization
 * 
 * @module RoleModal
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useBodyScrollLock from '../../../../hooks/useBodyScrollLock';
import { createEmptyPermissions } from '../../utils/permissionHelpers';
import './RoleModal.scss';

const RoleModal = ({ visible, role, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    permissions: createEmptyPermissions()
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = useMemo(() => !!role, [role]);
  
  const isLocked = useMemo(() => {
    if (!role) return false;
    return role.id === 1 || role.name.toLowerCase() === 'super admin';
  }, [role]);
  
  // Lock body scroll when modal is open
  useBodyScrollLock(visible, ['.role-modal', '.role-modal-backdrop']);
  
  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name || '',
        permissions: role.permissions || createEmptyPermissions()
      });
    } else {
      setFormData({
        name: '',
        permissions: createEmptyPermissions()
      });
    }
    setErrors({});
    setIsSubmitting(false);
  }, [role]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && visible && !isSubmitting) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, onClose, isSubmitting]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors]);

  /**
   * Validate form data
   */
  const validate = useCallback(() => {
    const newErrors = {};

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Role name must be at least 3 characters';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Role name must be less than 50 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error submitting role:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validate, onSave, isSubmitting]);

  /**
   * Handle backdrop click to close modal
   */
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  }, [onClose, isSubmitting]);

  if (!visible) return null;

  return (
    <div 
      className="role-modal-backdrop" 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="role-modal-title"
    >
      <div className="role-modal">
        {/* Modal Header */}
        <div className="role-modal__header">
          <h2 id="role-modal-title" className="role-modal__title">
            {isEditMode ? 'Edit Role' : 'Create New Role'}
          </h2>
          <button 
            className="role-modal__close" 
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close modal"
            type="button"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Modal Body */}
        <form className="role-modal__body" onSubmit={handleSubmit}>
          <div className="role-modal__section">
            <div className="role-modal__form-row">
              <div className="role-modal__form-group full-width">
                <label htmlFor="role-name" className="role-modal__label">
                  Role Name <span className="role-modal__required">*</span>
                </label>
                <input
                  type="text"
                  id="role-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`role-modal__input ${errors.name ? 'role-modal__input--error' : ''}`}
                  placeholder="Enter role name"
                  disabled={isLocked || isSubmitting}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'role-name-error' : undefined}
                  autoFocus={!isEditMode}
                />
                {errors.name && (
                  <span id="role-name-error" className="role-modal__error" role="alert">
                    <i className="bi bi-exclamation-circle"></i> {errors.name}
                  </span>
                )}
                <p className="role-modal__hint">
                  Choose a descriptive name for this role (display order will be assigned automatically)
                </p>
              </div>
            </div>
          </div>

          {isLocked && (
            <div className="role-modal__info">
              <i className="bi bi-info-circle"></i>
              <span>This is a system role and cannot be modified.</span>
            </div>
          )}

          {!isEditMode && (
            <div className="role-modal__info">
              <i className="bi bi-lightbulb"></i>
              <span>
                After creating the role, you can configure its permissions in the permissions table.
              </span>
            </div>
          )}
        </form>

        {/* Modal Footer */}
        <div className="role-modal__footer">
          <button
            type="button"
            className="role-modal__btn role-modal__btn--cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <i className="bi bi-x-circle"></i>
            Cancel
          </button>
          <button
            type="submit"
            className="role-modal__btn role-modal__btn--primary"
            onClick={handleSubmit}
            disabled={isSubmitting || isLocked}
          >
            {isSubmitting ? (
              <>
                <i className="bi bi-arrow-repeat spin"></i>
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="bi bi-check-circle"></i>
                {isEditMode ? 'Update Role' : 'Create Role'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleModal;

